import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { apiRequest, ApiError, registerSessionSync } from '../utils/apiClient';
import { readPersistedValue, writePersistedValue } from '../utils/sessionPersistence';

const SESSION_STORAGE_KEY = 'safaqat24.auth.session';

type Nullable<T> = T | null;

export type AccountType = 'individual' | 'broker' | 'company' | 'developer';

export type FalVerificationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export type NafathVerificationStatus =
  | 'NOT_STARTED'
  | 'PENDING'
  | 'VERIFIED'
  | 'FAILED'
  | 'EXPIRED';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  role?: string;
  accountType?: AccountType;
  companyName?: string;
  avatarUrl?: string | null;
  // Self-reported demographics (Statistics platform)
  gender?: string | null;
  nationality?: string | null;
  birthYear?: number | null;
  // Broker-specific fields (REGA requirements)
  nationalId?: string;
  falLicenseNumber?: string;
  officeAddress?: string;
  // Company-specific fields
  crNumber?: string;
  regaLicense?: string;
  vatNumber?: string;
  // FAL verification status (returned by /auth/me for broker/company users)
  falVerificationStatus?: FalVerificationStatus;
  falRejectionReason?: string;
  agentId?: string;
  // True for a plain individual (role USER) who has listed a property under the
  // platform FAL license — used to badge them and unlock owner add-ons.
  hasFalListing?: boolean;
  // Nafath identity verification (returned by /auth/me for broker/agent users)
  nafathVerificationStatus?: NafathVerificationStatus;
  nafathVerifiedAt?: string;
  nafathRejectionReason?: string;
  nationalIdLastFour?: string | null;
  // Admin TOTP enrollment flag — set on login when enrollment is required
  mustEnrollTotp?: boolean;
  // Common fields
  createdAt?: string;
  emailVerified?: boolean;
  emailVerifiedAt?: string;
  lastLoginAt?: string;
  phoneNumber?: string;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface StoredSession {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: AuthUser;
}

interface SignUpResponse {
  requiresVerification: boolean;
  email: string;
  message: string;
  expiresIn?: number;
}

export interface SignInResult {
  requiresTotp?: boolean;
  totpToken?: string;
  mustEnrollTotp?: boolean;
}

export interface VerifyTotpResult {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: AuthUser;
  backupCodeUsed?: boolean;
  backupCodesRemaining?: number;
}

interface AuthContextType {
  user: Nullable<AuthUser>;
  session: Nullable<StoredSession>;
  loading: boolean;
  error: Nullable<string>;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verifyTotp: (totpToken: string, code: string) => Promise<void>;
  clearMustEnrollTotp: () => void;
  signUp: (
    email: string,
    password: string,
    userData?: {
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
      accountType?: AccountType;
      // Company-specific fields
      companyName?: string;
      crNumber?: string;
      regaLicense?: string;
      vatNumber?: string;
      // Broker-specific fields (REGA requirements)
      nationalId?: string;
      falLicenseNumber?: string;
      officeAddress?: string;
      // Self-reported demographics (optional)
      gender?: string;
      nationality?: string;
      birthYear?: number;
      // Intent-first signup (services-expansion)
      accountKind?: 'individual' | 'company';
      declaredRoles?: string[];
      isPropertyOwner?: boolean;
    }
  ) => Promise<SignUpResponse>;
  verifyRegistration: (email: string, otp: string) => Promise<StoredSession>;
  activateSession: (pendingSession: StoredSession) => void;
  resendRegistrationOtp: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateProfile: (updates: {
    firstName?: string;
    lastName?: string;
    email?: string;
    avatarUrl?: string | null;
    gender?: string;
    nationality?: string | null;
    birthYear?: number | null;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

async function readStoredSession(): Promise<Nullable<StoredSession>> {
  return readPersistedValue<StoredSession>(SESSION_STORAGE_KEY);
}

async function persistSession(session: Nullable<StoredSession>): Promise<void> {
  await writePersistedValue<StoredSession>(SESSION_STORAGE_KEY, session);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Nullable<StoredSession>>(null);
  const [user, setUser] = useState<Nullable<AuthUser>>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Nullable<string>>(null);

  // Keep React state in sync when apiClient silently refreshes (or drops) the
  // session on a 401. apiClient owns localStorage; this just mirrors it into state.
  useEffect(() => {
    registerSessionSync({
      onRefreshed: (next) => {
        const nextSession = next as StoredSession;
        setSession(nextSession);
        setUser(nextSession.user);
      },
      onCleared: () => {
        setSession(null);
        setUser(null);
      },
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const restoreSession = async () => {
      const stored = await readStoredSession();
      if (!isMounted) {
        return;
      }

      if (!stored) {
        setLoading(false);
        return;
      }

      setSession(stored);
      setUser(stored.user);
      setLoading(false);

      try {
        const { user: refreshedUser } = await apiRequest<{ user: AuthUser }>('/auth/session', {
          method: 'GET',
          token: stored.token,
        });
        const nextSession: StoredSession = { ...stored, user: refreshedUser };
        setSession(nextSession);
        setUser(refreshedUser);
        await persistSession(nextSession);
      } catch (authError) {
        console.warn('Session refresh failed, clearing stored state.', authError);
        setSession(null);
        setUser(null);
        await persistSession(null);
      }
    };

    restoreSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const setSessionState = useCallback((value: Nullable<StoredSession>) => {
    setSession(value);
    setUser(value?.user ?? null);
    void persistSession(value);
  }, []);

  const clearMustEnrollTotp = useCallback(() => {
    setSession((prev) => {
      if (!prev) return prev;
      const nextUser: AuthUser = { ...prev.user, mustEnrollTotp: false };
      const nextSession: StoredSession = { ...prev, user: nextUser };
      void persistSession(nextSession);
      return nextSession;
    });
    setUser((prev) => (prev ? { ...prev, mustEnrollTotp: false } : prev));
  }, []);

  const signIn = useMemo(
    () => async (email: string, password: string): Promise<SignInResult> => {
      setError(null);
      try {
        const payload = await apiRequest<
          StoredSession & {
            refreshToken?: string;
            requiresTotp?: boolean;
            totpToken?: string;
            mustEnrollTotp?: boolean;
          }
        >(
          '/auth/login',
          {
            method: 'POST',
            body: { email: email.trim().toLowerCase(), password },
          }
        );

        // Admin with TOTP enrolled — login not complete yet; caller must call verifyTotp
        if (payload.requiresTotp && payload.totpToken) {
          return { requiresTotp: true, totpToken: payload.totpToken };
        }

        // If the backend signals enrollment is required, stamp that flag onto the
        // stored user so AdminGuard can redirect reactively on any /admin/* route.
        const sessionUser: AuthUser = payload.mustEnrollTotp
          ? { ...payload.user, mustEnrollTotp: true }
          : payload.user;

        const nextSession: StoredSession = {
          token: payload.token,
          refreshToken: payload.refreshToken,
          expiresAt: payload.expiresAt,
          user: sessionUser,
        };
        setSessionState(nextSession);

        // Admin without TOTP enrolled — must set up 2FA
        if (payload.mustEnrollTotp) {
          return { mustEnrollTotp: true };
        }

        return {};
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Unable to sign in. Please try again later.';
        setError(message);
        throw err;
      }
    },
    [setSessionState]
  );

  const verifyTotp = useMemo(
    () => async (totpToken: string, code: string): Promise<void> => {
      setError(null);
      try {
        const payload = await apiRequest<{
          accessToken?: string;
          token?: string;
          refreshToken?: string;
          expiresAt?: string;
          user: AuthUser;
          backupCodeUsed?: boolean;
          backupCodesRemaining?: number;
        }>(
          '/auth/totp/verify',
          {
            method: 'POST',
            body: { totpToken, code },
          }
        );

        // /auth/totp/verify returns accessToken (camelCase); normalise to token
        const nextSession: StoredSession = {
          token: payload.accessToken ?? payload.token ?? '',
          refreshToken: payload.refreshToken,
          expiresAt: payload.expiresAt,
          user: payload.user,
        };
        setSessionState(nextSession);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'TOTP verification failed. Please try again.';
        setError(message);
        throw err;
      }
    },
    [setSessionState]
  );

  const signUp = useMemo(
    () => async (
      email: string,
      password: string,
      userData?: {
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
        accountType?: AccountType;
        // Company-specific fields
        companyName?: string;
        crNumber?: string;
        regaLicense?: string;
        vatNumber?: string;
        // Broker-specific fields (REGA requirements)
        nationalId?: string;
        falLicenseNumber?: string;
        officeAddress?: string;
        // Developer-specific fields
        developerName?: string;
        developerCity?: string;
        developerFoundedYear?: number;
        developerLogoUrl?: string;
        // Self-reported demographics (Statistics platform) — all optional
        gender?: string;
        nationality?: string;
        birthYear?: number;
        // Intent-first signup (services-expansion) — declared professional roles
        // + property-owner tag; the backend persists these into User.metadata and
        // the actual AccountActivity rows/company are created post-verify.
        accountKind?: 'individual' | 'company';
        declaredRoles?: string[];
        isPropertyOwner?: boolean;
      }
    ): Promise<SignUpResponse> => {
      setError(null);
      try {
        // Build company_profile for company registrations (backend expects snake_case)
        const companyProfile = userData?.accountType === 'company' && userData?.companyName
          ? {
              company_name: userData.companyName,
              name: userData.companyName, // Backend also checks 'name'
              cr_number: userData.crNumber,
              rega_license: userData.regaLicense,
              vat_number: userData.vatNumber,
              email: email.trim().toLowerCase(),
            }
          : undefined;

        // Build broker_profile for broker registrations
        const brokerProfile = userData?.accountType === 'broker'
          ? {
              national_id: userData.nationalId,
              fal_license_number: userData.falLicenseNumber,
              office_address: userData.officeAddress,
            }
          : undefined;

        // Build developer_profile for real-estate developer registrations
        const developerProfile = userData?.accountType === 'developer' && userData?.developerName
          ? {
              developer_name: userData.developerName,
              cr_number: userData.crNumber,
              rega_license: userData.falLicenseNumber,
              contact_phone: userData.nationalId,
              logo_url: userData.developerLogoUrl,
              headquarters_city: userData.developerCity,
              founded_year: userData.developerFoundedYear,
            }
          : undefined;

        const payload = await apiRequest<{
          requiresVerification?: boolean;
          email: string;
          message: string;
          expiresIn?: number;
        }>(
          '/auth/register',
          {
            method: 'POST',
            body: {
              email: email.trim().toLowerCase(),
              password,
              firstName: userData?.firstName,
              lastName: userData?.lastName,
              profile: {
                firstName: userData?.firstName,
                lastName: userData?.lastName,
              },
              phone: userData?.phoneNumber || userData?.nationalId, // prefer explicit phone
              // Backend expects 'role' not 'accountType'
              role: userData?.accountType || 'individual',
              accountType: userData?.accountType || 'individual',
              // Company profile in expected format
              company_profile: companyProfile,
              // Broker profile (if applicable)
              broker_profile: brokerProfile,
              // Developer profile (if applicable)
              developer_profile: developerProfile,
              // Also send FAL license at top level for brokers
              falLicenseNumber: userData?.falLicenseNumber,
              // Self-reported demographics (optional)
              gender: userData?.gender,
              nationality: userData?.nationality,
              birthYear: userData?.birthYear,
              // Intent-first signup extras (mobile-compat /register passthrough)
              accountKind: userData?.accountKind,
              declaredRoles: userData?.declaredRoles,
              isPropertyOwner: userData?.isPropertyOwner,
            },
          }
        );

        // Registration now requires email verification
        return {
          requiresVerification: payload.requiresVerification ?? true,
          email: payload.email,
          message: payload.message,
          expiresIn: payload.expiresIn,
        };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Unable to create account. Please try again later.';
        setError(message);
        throw err;
      }
    },
    []
  );

  const verifyRegistration = useMemo(
    () => async (email: string, otp: string): Promise<StoredSession> => {
      setError(null);
      try {
        const payload = await apiRequest<StoredSession & { refreshToken?: string }>(
          '/auth/verify-registration',
          {
            method: 'POST',
            body: {
              email: email.trim().toLowerCase(),
              otp,
            },
          }
        );

        // Return session data without activating it — the registration wizard
        // will activate the session only after the full flow completes
        return {
          token: payload.token,
          refreshToken: payload.refreshToken,
          expiresAt: payload.expiresAt,
          user: payload.user,
        };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Verification failed. Please try again.';
        setError(message);
        throw err;
      }
    },
    []
  );

  const activateSession = useCallback((pendingSession: StoredSession) => {
    setSessionState(pendingSession);
  }, [setSessionState]);

  const resendRegistrationOtp = useMemo(
    () => async (email: string) => {
      setError(null);
      try {
        await apiRequest('/auth/resend-registration-otp', {
          method: 'POST',
          body: { email: email.trim().toLowerCase() },
        });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Failed to resend verification code.';
        setError(message);
        throw err;
      }
    },
    []
  );

  const signOut = useMemo(
    () => async () => {
      const currentToken = session?.token;
      setError(null);
      setSessionState(null);

      if (!currentToken) {
        return;
      }

      try {
        await apiRequest('/auth/logout', {
          method: 'POST',
          token: currentToken,
        });
      } catch (err) {
        console.warn('Failed to notify API about logout', err);
      }
    },
    [session?.token, setSessionState]
  );

  const resetPassword = useMemo(
    () => async (email: string) => {
      setError(null);
      try {
        await apiRequest('/auth/reset-password', {
          method: 'POST',
          body: { email: email.trim().toLowerCase() },
        });
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Unable to send password reset email.';
        setError(message);
        throw err;
      }
    },
    []
  );

  const updateProfile = useMemo(
    () => async ({ firstName, lastName, email: nextEmail, avatarUrl, gender, nationality, birthYear }: {
      firstName?: string;
      lastName?: string;
      email?: string;
      avatarUrl?: string | null;
      gender?: string;
      nationality?: string | null;
      birthYear?: number | null;
    }) => {
      if (!session) {
        throw new Error('No active session');
      }

      setError(null);
      try {
        const { user: updatedUser } = await apiRequest<{ user: AuthUser }>(
          '/auth/profile',
          {
            method: 'PUT',
            token: session.token,
            body: {
              firstName,
              lastName,
              email: nextEmail?.trim().toLowerCase(),
              avatarUrl,
              gender,
              nationality,
              birthYear,
            },
          }
        );

        const nextSession: StoredSession = {
          ...session,
          user: {
            ...session.user,
            ...updatedUser,
          },
        };
        setSessionState(nextSession);
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Failed to update profile information.';
        setError(message);
        throw err;
      }
    },
    [session, setSessionState]
  );

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      session,
      loading,
      error,
      signIn,
      verifyTotp,
      clearMustEnrollTotp,
      signUp,
      verifyRegistration,
      activateSession,
      resendRegistrationOtp,
      signOut,
      resetPassword,
      updateProfile,
    }),
    [user, session, loading, error, signIn, verifyTotp, clearMustEnrollTotp, signUp, verifyRegistration, activateSession, resendRegistrationOtp, signOut, resetPassword, updateProfile]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export async function enrollTotpInit(token: string) {
  return apiRequest<{ sessionId: string; qrDataUrl: string; base32Secret: string }>(
    '/auth/totp/enroll/init',
    { method: 'POST', token, body: {} }
  );
}

export async function enrollTotpConfirm(token: string, sessionId: string, code: string) {
  return apiRequest<{ backupCodes: string[] }>(
    '/auth/totp/enroll/confirm',
    { method: 'POST', token, body: { sessionId, code } }
  );
}